import React, { ReactElement, useEffect, useState } from "react";
import styles from "./HomePage.module.scss";
import { Group, Modal, Stack } from "@mantine/core";
import Button from "../../components/Button/Button.component";
import AprTable from "../../components/AprTable/AprTable";
import { useQuery } from "@tanstack/react-query";
import { QueryKey, EarningsProtocolType } from "../../models/enums";
import { AdvisorResponse, ProviderInfo } from "../../models/ProviderInfo";
import { AdvisorService } from "../../services/Advisor.service";
import { Validator, ValidatorsInfo } from "../../models/Validator";
import { Node, NodesList, RelatedNode } from "../../models/Node";
import {
  formatNumber,
  getYearlyROI,
  isOutlierYearlyApr,
} from "../../utils/formatter";
import DelegateModal from "../../components/DelegateModal/DelegateModal";
import StakeModal from "../../components/StakeModal/StakeModal";
import ModalService from "../../utils/modal.service";
import { useStore } from "../../utils/store.service";
import FtsoService from "../../utils/ftso.service";
import {
  StakedTransaction,
  StakedTransactions,
} from "../../models/StakedTransaction";
import { EarningsData, AprUnavailableReason } from "../../models/EarningsData";
import { AddressPair } from "../../models/AddressPair";
import { BalanceObject } from "../../models/BalanceObject";
import CopyTextComponent from "../../components/copyText/CopyText.component";

export default function HomePage(): ReactElement | null {
  const [validators, setValidators] = useState<Validator[]>([]);
  const [providers, setProviders] = useState<ProviderInfo[]>([]);
  const [openedDelegate, setOpenedDelegate] = useState<boolean>(false);
  const [openedStake, setOpenedStake] = useState<boolean>(false);

  const [maxReturn, setMaxReturn] = useState<number | null>(null);
  const [totalAprEarnings, setTotalAprEarnings] = useState<number | null>(null);

  const urlParams = new URLSearchParams(window.location.search);
  const color: string | null = urlParams.get("color");
  const embed: boolean = !!urlParams.get("embed");

  if (color) {
    document.documentElement.style.setProperty("--quinary", `#${color}`);
  }

  const store = useStore();

  const { data: providersData } = useQuery({
    queryKey: [QueryKey.providersInfo],
    queryFn: async (): Promise<AdvisorResponse> =>
      AdvisorService.getDataProvidersInfo(),
  });

  const { data: validatorsInfo } = useQuery({
    queryKey: [QueryKey.validatorList],
    queryFn: (): Promise<ValidatorsInfo> => AdvisorService.getValidatorList(),
  });

  const { data: nodesList } = useQuery({
    queryKey: [QueryKey.nodes],
    queryFn: (): Promise<NodesList> => AdvisorService.getNodes(),
  });

  const getEnrichStakesData = (
    stakes: StakedTransaction[],
    nodes: Node[]
  ): EarningsData[] => {
    if (stakes.length === 0) {
      return [];
    }
    return stakes
      .map((stake: StakedTransaction) => {
        const node = nodes.find((n: Node) => n.nodeId === stake.node_id);
        const obj: EarningsData = {
          protocol: EarningsProtocolType.STAKE,
          provider: node?.name ?? "n/a",
          amount: stake.amount * 1e3,
          apr: getYearlyROI((node?.roi ?? 0) / 1e2),
        };
        return obj;
      })
      .filter((data): data is EarningsData => data !== null)
      .sort((a: any, b: any) => (b.roi ?? 0) - (a.roi ?? 0));
  };

  const getEnrichDelegationsData = (
    delegations: any,
    providersData: ProviderInfo[]
  ): EarningsData[] => {
    return delegations
      .map((delegation: any) => {
        const provider = providersData.find(
          (p: ProviderInfo) =>
            p.address.toLowerCase() === delegation.address.toLowerCase() // Use lowercase because of different adress formatting
        );

        let delegationsValue = delegation.value ?? 0;

        // Convert to wei
        delegationsValue = delegationsValue * 1e12;

        // Resolve APR + the reason it's unavailable, so the table can show the
        // right "N/A" tooltip.
        const yearlyApr = provider?.yearlyApr;
        let apr: number | null;
        let aprUnavailableReason: AprUnavailableReason | undefined;
        if (yearlyApr === null || yearlyApr === undefined) {
          apr = null;
          aprUnavailableReason = "no-data";
        } else if (isOutlierYearlyApr(yearlyApr)) {
          apr = null;
          aprUnavailableReason = "outlier";
        } else {
          apr = yearlyApr / 100;
        }

        // If node found  provider = node name else provider = delegations address
        const obj: EarningsData = {
          protocol: EarningsProtocolType.DELEGATION,
          provider: provider?.name ?? delegation.address ?? "",
          amount: delegationsValue ?? 0,
          apr,
          aprUnavailableReason,
        };
        return obj;
      })
      .filter((data: any): data is EarningsData => data !== null)
      .sort((a: any, b: any) => (b.roi ?? 0) - (a.roi ?? 0));
  };

  const calcAprEarningsPercentage = (earningsData: EarningsData[]): void => {
    // Calculate the total APR earnings percentage. Outlier APRs are already
    // nulled upstream, so (apr ?? 0) treats them as 0% here (no blow-up).
    const numerator = earningsData.reduce(
      (sum, protocol) => sum + ((protocol.apr ?? 0) / 100) * protocol.amount,
      0
    );

    // Sum everything
    const denominator = earningsData.reduce((sum, protocol) => {
      return sum + protocol.amount;
    }, 0);

    setTotalAprEarnings(Math.round((numerator / denominator) * 1000000));
  };

  const { data: earningsData, isLoading: isEarningsDataLoading } = useQuery({
    queryKey: [QueryKey.earningsData, store.account.get()],
    async queryFn(): Promise<EarningsData[] | []> {
      const accountName = store.account.get();

      if (accountName === "") {
        return [];
      }
      const allEarningsData: EarningsData[] = [];
      let nonDelegated = 0;
      let stakeTransactions: StakedTransactions | null = null;

      //Balance object -> {balance: number, balanceWrapped: number}
      const balanceObject: BalanceObject | null =
        await FtsoService.getBalance(accountName);

      //GET address pair AddressPair IS NEEDED TO GET STAKES AND DELEGATIONS
      const addressPair: AddressPair =
        await AdvisorService.getAddressPair(accountName);

      if (addressPair) {
        // Get STAKES
        const getStakeTransactions = await AdvisorService.getStakedTransactions(
          addressPair.bechAddress
        );

        if (getStakeTransactions) {
          const data = getEnrichStakesData(
            getStakeTransactions.stakings,
            nodesList?.nodes || []
          );
          allEarningsData.push(...data);
          stakeTransactions = getStakeTransactions;
        }
      }

      //Get DELEGATIONS
      const delegations = await FtsoService.getDelegations(accountName);
      let delegationSum = 0;
      if (delegations && delegations.length > 0 && providersData) {
        const data = nodesList
          ? getEnrichDelegationsData(
              delegations,
              providersData.delegation.topFtsos
            )
          : [];
        allEarningsData.push(...data);

        // Get total delegations amount
        delegationSum = data.reduce(
          (sum, delegation) => sum + delegation.amount,
          0
        );
      }
      nonDelegated = balanceObject?.balanceWrapped * 1e12 - delegationSum;

      // NONE protocol is the unwrapped balance and available balance on c-chain  + unstaked balance on p-chain
      const noneValue =
        balanceObject?.balance * 1e12 +
        nonDelegated +
        (stakeTransactions?.available_balance ?? 0) * 1e3;

      const noneEarnings: EarningsData = {
        protocol: EarningsProtocolType.NONE,
        provider: "",
        amount: noneValue,
        apr: 0,
      };
      allEarningsData.push(noneEarnings);

      // Calculate and set the total APR earnings  (sum of all APRs * amount) / sum of all amounts
      calcAprEarningsPercentage(allEarningsData);

      return allEarningsData;
    },
    enabled:
      !!nodesList &&
      store.accountConnected.get() &&
      !!providersData &&
      store.account.get() !== "",
  });

  function enrichValidatorsData(
    validators: Validator[],
    nodes: Node[],
    totalStake: number
  ): void {
    let enrichedValidators = validators.map((validator: Validator) => {
      const capLimitFactor = 0.05;
      const node = nodes.find((n: Node) => n.nodeId === validator.nodeID);

      let totalFtsoStake = validator.totalStake;
      if (!node) {
        validator.capLimit = capLimitFactor * totalStake - totalFtsoStake;
        return validator;
      }

      node.relatedNodes.forEach((relatedNode: RelatedNode) => {
        const relatedValidator = validators.find(
          (val: Validator) => val.nodeID === relatedNode.nodeId
        );
        if (relatedValidator) {
          totalFtsoStake += relatedValidator.totalStake;
        }
      });

      if (node) {
        validator.roi = getYearlyROI((node?.roi ?? 0) / 1e2),
        validator.name = node.name;
        validator.ftsoAddress = node.ftsoAddress;
        validator.capLimit = capLimitFactor * totalStake - totalFtsoStake;
      }
      return validator;
    });
    enrichedValidators.sort(
      (a: Validator, b: Validator) => (b.roi ?? 0) - (a.roi ?? 0)
    );
    enrichedValidators = enrichedValidators.filter((item: Validator) => {
      const timeLeftInMs: number =
        new Date(item.endTime).getTime() - new Date().getTime();
      const validTime: boolean = timeLeftInMs > 14 * 24 * 60 * 60 * 1000;
      return item.freeSpace / 1e9 > 50000 && item.uptime > 0.9 && validTime;
    });

    setValidators(enrichedValidators);
  }

  useEffect(() => {
    if (validatorsInfo && nodesList) {
      enrichValidatorsData(
        validatorsInfo.validators,
        nodesList.nodes,
        validatorsInfo.total
      );
    }
  }, [validatorsInfo, nodesList]);


  // Calculate apy
  useEffect(() => {
    let providersRoi: number = 0;
    if (providersData) {
      const filteredProviders = providersData.delegation.topFtsos.filter(
        (item: ProviderInfo) => item.listed
      );

      setProviders(filteredProviders);
      // Skip providers with an unreliable/outlier APR so it can't inflate the
      // displayed max return.
      providersRoi =
        filteredProviders.find((p) => !isOutlierYearlyApr(p.yearlyApr))
          ?.yearlyApr ?? 0;
    }

    if (validators && validators.length > 0 ) {
      const validatorsRoi: number = validators[0]?.roi ?? 0;
      setMaxReturn(Math.max(validatorsRoi * 100, providersRoi));
    }
  }, [validators, providersData]);

  return (
    <>
      <Modal
        centered
        opened={openedDelegate}
        onClose={() => setOpenedDelegate(false)}
        title="How to delegate"
        closeButtonProps={{
          size: 40,
          mt: -15,
          mr: -5,
        }}
      >
        <DelegateModal />
      </Modal>

      <Modal
        centered
        opened={openedStake}
        onClose={() => setOpenedStake(false)}
        title="How to stake"
        closeButtonProps={{
          size: 40,
          mt: -15,
          mr: -5,
        }}
      >
        <StakeModal />
      </Modal>


      <div className={styles.homeContent}>
        <Stack className={styles.textIntro}>
          <span className={embed ? styles.grayHeading : styles.heading}>
            You could earn up to{" "}
            <b>
              {maxReturn ? formatNumber(maxReturn * 100, false, 0) : "N/A"}%
            </b>
            * annually.
          </span>
          {!embed && (
            <p className={styles.primaryText}>
              By owning Flare tokens, you can contribute to the decentralization
              and stability of the network by either delegating wrapped FLR to
              FTSO data providers or staking FLR to validator nodes and
              receiving rewards for your participation.
            </p>
          )}
        </Stack>
        {!embed && (
          <>
            <div className={styles.horizontalLine}></div>

            <div
              className={
                store.accountConnected.get() ? "" : styles.connectedContainer
              }
            >
              {store.accountConnected.get() ? (
                <>
                  <Stack gap={5}>
                    <span className={styles.grayHeading}>
                      You are currently earning {totalAprEarnings ?? "X"}%
                    </span>
                    <div
                      className={`${styles.darkerFinePrint} ${styles.connectedWithContainer} `}
                    >
                      <span>Connected with </span>{" "}
                      <CopyTextComponent
                        weight={"bold"}
                        primaryColor={false}
                        link={
                          "https://flare-explorer.flare.network/address/" +
                          store.account.get()
                        }
                        text={store.account.get()}
                        value={store.account.get()}
                      ></CopyTextComponent>
                    </div>
                  </Stack>
                  <div className={styles.earningsTablesContainer}>
                    <div className={styles.mobileEarningsTableContainer}>
                      {isEarningsDataLoading ? (
                        <p>Loading..</p>
                      ) : (
                        <AprTable type={"earnings"} earnings={earningsData} />
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <div className={styles.connectButtonContainer}>
                  <Button
                    onClick={() => ModalService.openModal("walletConnectModal")}
                    addStyles={["full"]}
                  >
                    Connect wallet
                  </Button>
                </div>
              )}
            </div>
            <Stack gap={5}>
              <span className={styles.heading}>
                Earnings expectations by protocol
              </span>
              <span className={styles.finePrint}>
                By using this site, you Confirm that you have read and agree
                with the{" "}
                <a href="https://flare.space/terms-of-service">Disclaimer</a>.
              </span>
            </Stack>
          </>
        )}

        <div className={styles.aprTablesContainer}>
          <div className={styles.mobileTableContainer}>
            <Stack gap="xs" className={styles.mobileTableStackWidth}>
              <Group justify={"space-between"}>
                <h2>Delegate to best FTSO providers</h2>
                <Button
                  addStyles={["basic", "compact", "bold", "shortLineHeight"]}
                  onClick={() => setOpenedDelegate(true)}
                >
                  How to delegate
                </Button>
              </Group>
              <AprTable
                type={"ftso"}
                providers={providers}
                registeredFtsoCount={
                  providersData?.delegation.numOfRegisteredFtsos
                }
                embed={embed}
              />
            </Stack>
            <Group
              w={"14%"}
              justify={"center"}
              pt={175}
              wrap={"nowrap"}
              className={styles.hideOnMobile}
            >
              <div className={styles.midLineLeft}></div>
              <span className={styles.midText}>
                <i>or</i>
              </span>
              <div className={styles.midLineRight}></div>
            </Group>
            <Stack gap="xs" className={styles.mobileTableStackWidth}>
              <Group justify={"space-between"} className={styles.groupStaking}>
                <h2>Stake to best validators</h2>
                <Button
                  addStyles={["basic", "compact", "bold", "shortLineHeight"]}
                  onClick={() => setOpenedStake(true)}
                >
                  How to stake
                </Button>
              </Group>
              <AprTable type={"stake"} validators={validators} embed={embed} />
            </Stack>
          </div>
           <div className={styles.explanationNote}>
            <p>
              * The APY (Annual Percentage Yield) calculation is performed by
              taking either the best validator APY or FTSO provider APY.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
